import { cn } from "@/lib/cn";

/**
 * Common stagger timings for entrance animations across every slide
 * component. Keeps the rhythm consistent without each component re-inventing
 * its own animation schedule.
 */
export const SLIDE_STAGGER = ["0ms", "120ms", "240ms", "360ms", "480ms", "600ms", "720ms"];

export const Reveal = ({ children, delay = 0, className }) => (
  <div
    className={cn("opacity-0 animate-fade-up", className)}
    style={{
      animationDelay: SLIDE_STAGGER[delay] ?? SLIDE_STAGGER[SLIDE_STAGGER.length - 1],
      animationFillMode: "both",
    }}
  >
    {children}
  </div>
);

/**
 * Outer frame that every presentation slide is built on. Provides:
 *   - A constrained, generously padded container
 *   - A consistent test hook
 *   - Spacing that matches the rest of the design system
 *
 * Width tiers: `default` = 6xl portal width; `wide` = 7xl for dashboards;
 * `narrow` = 4xl for moments and quotes.
 */
export const SlideFrame = ({
  children,
  className,
  width = "default",
  testId,
  as: As = "section",
}) => {
  return (
    <As
      data-testid={testId}
      className={cn(
        "mx-auto flex w-full flex-col gap-10",
        width === "narrow" && "max-w-4xl",
        width === "default" && "max-w-6xl",
        width === "wide" && "max-w-7xl",
        className
      )}
    >
      {children}
    </As>
  );
};

/**
 * The standard slide header — eyebrow + headline + optional lede. Used at the
 * top of nearly every slide so that the rhythm of arrival is identical
 * regardless of slide type.
 */
export const SlideHeader = ({
  eyebrow,
  title,
  lede,
  align = "left",
  size = "lg",
  className,
  testId,
}) => {
  return (
    <header
      data-testid={testId || "slide-header"}
      className={cn(
        "flex flex-col gap-5",
        align === "center" && "items-center text-center",
        className
      )}
    >
      {eyebrow && (
        <Reveal delay={0}>
          <span className="haevn-eyebrow" data-testid="slide-eyebrow">
            {eyebrow}
          </span>
        </Reveal>
      )}
      {title && (
        <Reveal delay={1}>
          <h2
            data-testid="slide-title"
            className={cn(
              "max-w-3xl text-balance font-medium tracking-tightest text-foreground leading-[1.05]",
              size === "lg" && "text-3xl sm:text-4xl md:text-5xl",
              size === "xl" && "text-4xl sm:text-5xl md:text-6xl",
              align === "center" && "mx-auto"
            )}
          >
            {title}
          </h2>
        </Reveal>
      )}
      {lede && (
        <Reveal delay={2}>
          <p
            data-testid="slide-lede"
            className={cn(
              "max-w-2xl text-pretty text-base leading-relaxed text-muted-foreground sm:text-lg",
              align === "center" && "mx-auto"
            )}
          >
            {lede}
          </p>
        </Reveal>
      )}
    </header>
  );
};

export default SlideFrame;
